import type {
  ChangePasswordFormData,
  EmailRegisterFormData,
  EmailSignInFormData,
  UpdateProfileFormData,
} from "~/types/Data/user/formData";
import type {
  UserState,
  SaveUserResponse,
  UserLoginResponse,
  UserRegisterResponse,
} from "~/types/Data/user/user";
import paths from "~/constants/paths";
import useCart from "~/composables/checkout/useCart";

export default function useUser() {
  const user = useState<UserState | null>("user", () => null);

  const { resetCart } = useCart();
  const { addToast } = useToast();
  const { trackRegistration, trackLogin } = useTrackEvents();

  const router = useRouter();
  const userToken = useCookie("hs-token");
  const verifiedEmail = useSessionStorage<string>("verified-email", "");
  const verifiedMobile = useSessionStorage("verified-mobile", {});

  const error = ref<string>();
  const loading = ref(false);

  const initUser = async (baseUrl: string) => {
    if (userToken.value) {
      try {
        const { res } = await useApi<SaveUserResponse>(
          `/api/v1/me`,
          {
            authToken: userToken.value,
          },
          {
            baseUrl,
          },
        );

        if (res) {
          user.value = {
            ...res.data,
            token: userToken.value,
          };
        }
      } catch (err) {
        logout();
        router.push(paths.login);
      }
    }
  };

  const login = async (credentials: EmailSignInFormData) => {
    loading.value = true;
    error.value = undefined;

    const { res, err } = await useApi<UserLoginResponse>(
      `/api/v1/login`,
      {
        method: "POST",
        body: credentials,
      },
      { catchError: true },
    );

    if (res?.data) {
      user.value = {
        ...res.data.user,
        token: res.data.access_token,
      };

      userToken.value = res.data.access_token;

      trackLogin();

      if (user.value?.delete_request?.is_deletion_pending) {
        await router.push(paths.accountDeletionPending);
        return;
      }

      if (router.options.history.state.back) {
        router.replace(router.options.history.state.back.toString());
      } else {
        router.push(paths.home);
      }
    }

    if (err) {
      logout();
      error.value = "Email/Password is incorrect";
    }

    loading.value = false;
  };

  const register = async (userData: EmailRegisterFormData) => {
    const { res, err } = await useApi<UserRegisterResponse>(
      "/api/v1/register",
      {
        method: "POST",
        body: {
          email: userData.email,
          password: userData.password,
          first_name: userData.first_name,
          last_name: userData.last_name,
          country_code: userData.mobile.country_code,
          mobile: userData.mobile.number,
          device_name: "postman", // for some reason this is required and fixed
        },
      },
      { catchError: true },
    );

    if (res?.data) {
      user.value = {
        ...res.data.user,
        token: res.data.token,
      };

      userToken.value = res.data.token;

      trackRegistration();

      router.push(paths.home);
    }

    if (err) {
      error.value = err.meta.errors[0].message;
      logout();
    }

    loading.value = false;
  };

  const logout = () => {
    user.value = null;
    userToken.value = null;
    resetCart();
    verifiedMobile.value = null;
    verifiedEmail.value = null;
  };

  const update = async (baseUrl: string, userData: UpdateProfileFormData) => {
    if (userToken.value) {
      const { res } = await useApi<SaveUserResponse>(
        `/api/v1/me`,
        {
          method: "PUT",
          authToken: userToken.value,
          body: {
            first_name: userData.first_name,
            last_name: userData.last_name,
            email: userData.email.toLowerCase(),
            mobile: userData.mobile.number,
            country_code: userData.mobile.country_code,
          },
        },
        {
          baseUrl,
        },
      );

      if (res?.data) {
        user.value = {
          ...res.data,
          token: userToken.value,
        };
      }
    }
  };

  const validateMobile = async (country_code: string, mobile: string) => {
    await useApi(`/api/v1/validate`, {
      method: "POST",
      body: {
        country_code,
        mobile,
      },
    });
  };

  const changePassword = async (data: ChangePasswordFormData) => {
    loading.value = true;
    error.value = undefined;

    try {
      await useApi(`/api/v1/me/password/reset`, {
        method: "PUT",
        authToken: user.value?.token,
        body: {
          password: data.new_password,
          password_confirmation: data.confirm_new_password,
        },
      });
    } catch (err: any) {
      const apiError =
        err?.response?._data?.meta?.errors?.[0]?.message ||
        "An unexpected error occurred.";

      error.value = apiError;
    } finally {
      loading.value = false;
    }
  };

  const resetPassword = async (
    data: ChangePasswordFormData,
    email: string,
    resetToken: string,
  ) => {
    loading.value = true;
    error.value = undefined;

    const { err } = await useApi(
      "/api/v1/password-reset",
      {
        method: "POST",
        body: {
          email,
          token: resetToken,
          password: data.new_password,
          password_confirmation: data.confirm_new_password,
        },
      },
      { catchError: true },
    );

    if (err) error.value = err.meta.errors[0].message;

    loading.value = false;
  };

  const sendResetPasswordMail = async (email: string) => {
    loading.value = true;
    error.value = undefined;

    const { err } = await useApi(
      "/api/v1/password-email",
      {
        method: "POST",
        body: { email },
      },
      { catchError: true },
    );

    if (err) error.value = err.meta.errors[0].message;

    loading.value = false;
  };

  const deleteAccount = async (message: string) => {
    await useApi(`/api/v1/delete-user`, {
      method: "POST",
      authToken: userToken.value,
      body: {
        feedback: message,
      },
    });
  };

  const cancelDeleteRequest = async (deleteRequestId: string) => {
    if (!userToken.value) return;

    try {
      await useApi(`/api/v1/delete-requests/${deleteRequestId}`, {
        method: "DELETE",
        authToken: userToken.value,
      });

      router.replace(paths.home);

      addToast("Account deletion request cancelled successfully.", "success");
    } catch (err: any) {
      const message =
        err.response?._data?.meta?.errors?.[0]?.message ||
        "Failed to cancel deletion request";
      addToast(message, "error");
      throw err;
    }
  };

  return {
    user,
    loading,
    error,
    initUser,
    register,
    login,
    logout,
    update,
    validateMobile,
    changePassword,
    sendResetPasswordMail,
    resetPassword,
    deleteAccount,
    cancelDeleteRequest,
  };
}
